import React from "react";
import {COLUMN_CELL_INVALID_CONTENT} from "../../../columns";
import {DashboardCell} from "./DashboardCell";
import {METRICS_WITH_POTENTIAL_EMPTY_VALUES, EMPTY_METRIC_VALUE_TOOLTIP_KEYS} from "../../../metrics";
import {FaIcon} from "../../../../FaIcon";
import {withTranslation} from "react-i18next";

// Alto máximo del cartel del dato vacío, medido en pantalla (227px con el texto actual). Sirve para
// decidir si entra debajo de la celda o hay que abrirlo hacia arriba.
const EMPTY_TOOLTIP_HEIGHT = 240;

const GA4_EMPTY_TOOLTIP_KEY = 'dashboard.empty_metric_value_tooltip.ga4';
const GA4_ADGROUP_EMPTY_TOOLTIP_KEY = 'dashboard.empty_metric_value_tooltip.ga4_adgroup';

class DashboardSingleValueCellClass extends DashboardCell {
    constructor(props) {
        super(props);
        this.state = {tooltipUp: false};
    }

    // El cartel crece hacia abajo desde el tope de la celda, así que en las filas de la mitad
    // inferior de la tabla quedaba cortado por el borde de la ventana y se perdía el final del
    // texto. Acá se invierte cuando no hay lugar.
    //
    // Se mide al ABRIR el cartel (hover o foco) y no al renderizar a propósito: el Tablero dibuja
    // cientos de estas celdas y medir todas al montar costaría un reflow por celda para un cartel
    // que casi nunca se abre.
    handleTooltipEnter = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const tooltipUp = rect.top + EMPTY_TOOLTIP_HEIGHT > window.innerHeight;
        if (tooltipUp !== this.state.tooltipUp) {
            this.setState({tooltipUp});
        }
    };

    isEmptyValue() {
        const value = this.props.value;
        const columnData = this.props.columnData;
        
        // Verificar el valor formateado
        const isFormattedValueEmpty = value === null || value === undefined || value === "---" || value === "";
        
        // También verificar main_period_value si está disponible
        const isMainPeriodValueEmpty = columnData && (
            columnData.main_period_value === null || 
            columnData.main_period_value === undefined
        );
        
        return isFormattedValueEmpty && (columnData ? isMainPeriodValueEmpty : true);
    }

    shouldShowEmptyDataIcon() {
        if (!this.isEmptyValue()) {
            return false;
        }

        const metricName = this.props.metricName;
        const columnData = this.props.columnData;
        
        // Verificar si la métrica está en el array de métricas que pueden venir vacías
        if (!metricName || !METRICS_WITH_POTENTIAL_EMPTY_VALUES.includes(metricName)) {
            return false;
        }

        // Verificar que columnData tenga metric_name y coincida
        if (columnData && columnData.metric_name && columnData.metric_name === metricName) {
            return true;
        }

        return false;
    }

    getEmptyMetricTooltipKey() {
        const metricName = this.props.metricName;
        const key = EMPTY_METRIC_VALUE_TOOLTIP_KEYS[metricName] || 'dashboard.empty_metric_value_tooltip.default';
        // En un grupo de anuncios el motivo no es que GA4 no identifique la campaña: GA4 no
        // desglosa a ese nivel nunca, y el texto de las campañas lo explicaba mal.
        if (this.props.isAdgroupRow && key === GA4_EMPTY_TOOLTIP_KEY) {
            return GA4_ADGROUP_EMPTY_TOOLTIP_KEY;
        }
        return key;
    }

    renderEmptyDataIcon() {
        if (!this.shouldShowEmptyDataIcon()) {
            return null;
        }

        const {t} = this.props;
        const tooltipKey = this.getEmptyMetricTooltipKey();
        const tooltipText = t(tooltipKey);
        const tooltipClass = `warning-tooltip${this.state.tooltipUp ? ' -up' : ''}`;
        return (
            <div className="info-icon-container me-2"
                 tabIndex={0}
                 aria-label={tooltipText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                 onMouseEnter={this.handleTooltipEnter}
                 onFocus={this.handleTooltipEnter}>
                <FaIcon iconName="info-circle"/>
                <div className={tooltipClass}>
                    <div className="warning-tooltip-body">
                        <p className="warning-tooltip-text" dangerouslySetInnerHTML={{__html: tooltipText}}></p>
                    </div>
                </div>
            </div>
        );
    }

    renderContent() {
        const itemsToRender = [];
        const warningRendered = this.renderWarningIfNecessary();
        if(warningRendered !== null) {
            itemsToRender.push(warningRendered);
        }
        const liveMetaRendered = this.renderLiveMetaIfNecessary();
        if(liveMetaRendered !== null) {
            itemsToRender.push(liveMetaRendered);
        }

        if(this.props.value === COLUMN_CELL_INVALID_CONTENT && itemsToRender.length > 0){
            return itemsToRender;
        }

        // Si debe mostrar el icono de dato vacío, mostrar solo el icono. Salvo que el live_meta
        // ya explique el hueco («trayendo GA4…»): los dos juntos se contradicen, y el de dato
        // vacío dice que el tráfico «queda en la cuenta» cuando todavía no llegó.
        if (this.shouldShowEmptyDataIcon()) {
            if (liveMetaRendered !== null) {
                return itemsToRender;
            }
            itemsToRender.push(this.renderEmptyDataIcon());
            return itemsToRender;
        }

        itemsToRender.push(this.props.value);
        return itemsToRender;
    }

    render() {
        return <td className={`single-data ${this.processingCssClasses()}`}>{this.renderContent()}</td>;
    }
}

export const DashboardSingleValueCell = withTranslation()(DashboardSingleValueCellClass);
